import * as XLSX from 'xlsx';

// Parseo y validacion del Excel de carga masiva de convenios.

// El desplegable del Excel es una ayuda, no una garantia: se puede pegar texto
// encima y un .xls no lleva validaciones, asi que validarFilas los vuelve a exigir.
export const OPCIONES_ESTADO = ['Vigente', 'En renovación', 'Vencido', 'Próximo a vencer'];
export const OPCIONES_RENOVACION = ['Automática', 'Acuerdo por escrito de ambas partes', 'No indica', 'Indefinido'];
export const FORMATO_FECHA = 'DD/MM/AAAA';

// Definicion unica de la plantilla: el orden manda en el .xlsx y en la tabla, y
// `tipo` decide la validacion, tanto en el generador como en validarFilas.
export const COLUMNAS = [
  { clave: 'CODIGO', etiqueta: 'Código', ancho: 18, tipo: 'texto' },
  { clave: 'INSTITUCION', etiqueta: 'Institución', ancho: 40, tipo: 'texto' },
  { clave: 'PAIS', etiqueta: 'País(es)', ancho: 18, tipo: 'rango' },
  { clave: 'PROGRAMA_COORDINADOR', etiqueta: 'Programa / Coordinador', ancho: 30, tipo: 'texto' },
  { clave: 'FECHA_INICIO', etiqueta: 'Fecha de inicio', ancho: 16, tipo: 'fecha' },
  { clave: 'FECHA_FINALIZACION', etiqueta: 'Fecha de finalización', ancho: 20, tipo: 'fecha' },
  { clave: 'VIGENCIA', etiqueta: 'Vigencia (años)', ancho: 14, tipo: 'entero' },
  { clave: 'RENOVACION', etiqueta: 'Renovación', ancho: 30, tipo: 'lista', opciones: OPCIONES_RENOVACION },
  { clave: 'ESTADO', etiqueta: 'Estado', ancho: 18, tipo: 'lista', opciones: OPCIONES_ESTADO },
  { clave: 'SEGUIMIENTO', etiqueta: 'Seguimiento renovaciones / terminaciones', ancho: 34, tipo: 'texto' },
  { clave: 'OBSERVACION', etiqueta: 'Observación', ancho: 34, tipo: 'texto' },
];

export const MENSAJES = {
  vacio: 'El archivo adjunto está vacío.',
  formato: 'El archivo adjunto no tiene el formato correcto. Descargue la plantilla y no modifique los encabezados ni el orden de las columnas.',
  ilegible: 'No fue posible leer el archivo adjunto. Verifique que sea un Excel válido.',
};

export const CLAVES = COLUMNAS.map(({ clave }) => clave);

export const FILA_EJEMPLO = {
  CODIGO: 'CONV-2026-001',
  INSTITUCION: 'Universidad Nacional Autónoma de México',
  PAIS: 'México',
  PROGRAMA_COORDINADOR: 'Ingeniería Civil / Juan Pérez',
  FECHA_INICIO: '15/01/2026',
  FECHA_FINALIZACION: '15/01/2031',
  VIGENCIA: 5,
  RENOVACION: 'Automática',
  ESTADO: 'Vigente',
  SEGUIMIENTO: 'Sin novedades',
  OBSERVACION: 'Movilidad estudiantil y docente',
};

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

const plano = (v) => texto(v).toLowerCase()
  .replace(/[áàä]/g, 'a').replace(/[éèë]/g, 'e').replace(/[íìï]/g, 'i')
  .replace(/[óòö]/g, 'o').replace(/[úùü]/g, 'u').replace(/\s+/g, ' ');

const canonica = (valor, opciones) => {
  let p = plano(valor);
  let hallada = (opciones || []).filter((o) => plano(o) === p)[0];
  return hallada ? hallada : '';
};

// Acepta "5 años" ademas de "5".
const soloEntero = (valor) => {
  let m = texto(valor).match(/^(\d+)(\s*(años?|anos?|years?))?$/i);
  return m ? m[1] : '';
};

// La conversion a ms es UTC pero getDate() lee en hora local, asi que hay que
// reconstruir desde los componentes UTC o la fecha se corre un dia.
const desdeSerieExcel = (n) => {
  let u = new Date(Math.round((n - 25569) * 86400 * 1000));
  if (isNaN(u.getTime())) return null;
  return new Date(u.getUTCFullYear(), u.getUTCMonth(), u.getUTCDate());
};

const fechaValida = (d) => (d && !isNaN(d.getTime()) ? d : null);

export const parsearFecha = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  if (typeof valor === 'number') return desdeSerieExcel(valor);
  if (valor instanceof Date) return fechaValida(valor);

  let v = String(valor).trim();
  if (v === '') return null;

  let m = v.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return fechaValida(new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])));

  m = v.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (m) return fechaValida(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));

  if (/^\d+(\.\d+)?$/.test(v)) return desdeSerieExcel(Number(v));

  return null;
};

export const formatearFecha = (valor) => {
  let d = parsearFecha(valor);
  if (!d) return texto(valor);
  let dd = ('0' + d.getDate()).slice(-2);
  let mm = ('0' + (d.getMonth() + 1)).slice(-2);
  return dd + '/' + mm + '/' + d.getFullYear();
};

// Inversa de desdeSerieExcel; la plantilla la usa para escribir fechas de verdad.
export const aSerieExcel = (d) =>
  Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000) + 25569;

export const leerArchivo = (archivo) => new Promise((resolve, reject) => {
  let lector = new FileReader();

  lector.onerror = () => reject(new Error(MENSAJES.ilegible));

  lector.onload = ({ target }) => {
    let filas;
    try {
      let libro = XLSX.read(new Uint8Array(target.result), { type: 'array' });
      let nombre = libro.SheetNames[0];
      if (!nombre) return reject(new Error(MENSAJES.vacio));
      filas = XLSX.utils.sheet_to_json(libro.Sheets[nombre], { defval: '' });
    } catch (e) {
      return reject(new Error(MENSAJES.ilegible));
    }

    if (!filas || filas.length === 0) return reject(new Error(MENSAJES.vacio));

    // Antes de descartar filas vacias: con las columnas renombradas todas lo
    // parecerian y el mensaje seria "archivo vacio" en vez del de formato.
    let encabezados = Object.keys(filas[0]);
    let faltantes = CLAVES.filter((c) => encabezados.indexOf(c) < 0);
    if (faltantes.length > 0) return reject(new Error(MENSAJES.formato));

    let utiles = filas.filter((fila) => CLAVES.some((c) => texto(fila[c]) !== ''));
    if (utiles.length === 0) return reject(new Error(MENSAJES.vacio));

    resolve(utiles);
  };

  lector.readAsArrayBuffer(archivo);
});

// Unico punto que decide que es una fila valida. `n` arranca en 2 para que coincida
// con la fila real del Excel.
export const validarFilas = (filas, codigosExistentes, paises) => {
  let existentes = (codigosExistentes || []).map((c) => texto(c).toLowerCase()).filter(Boolean);
  let lista_paises = (paises || []).filter(Boolean);
  let vistos = {};

  return filas.map((cruda, i) => {
    let fila = { n: i + 2, error: '', campos_error: [] };
    CLAVES.forEach((c) => { fila[c] = texto(cruda[c]); });
    fila.FECHA_INICIO = formatearFecha(cruda.FECHA_INICIO);
    fila.FECHA_FINALIZACION = formatearFecha(cruda.FECHA_FINALIZACION);

    // Se normaliza antes de validar: se previsualiza y se envia el valor canonico.
    COLUMNAS.forEach(({ clave: c, tipo, opciones }) => {
      if (tipo !== 'lista') return;
      let canon = canonica(fila[c], opciones);
      if (canon) fila[c] = canon;
    });
    if (soloEntero(fila.VIGENCIA)) fila.VIGENCIA = soloEntero(fila.VIGENCIA);
    let pais_canonico = canonica(fila.PAIS, paises || []);
    if (pais_canonico) fila.PAIS = pais_canonico;

    let codigo = fila.CODIGO;
    let clave = codigo.toLowerCase();
    let inicio = parsearFecha(cruda.FECHA_INICIO);
    let fin = parsearFecha(cruda.FECHA_FINALIZACION);

    if (!codigo) {
      fila.error = 'El código del convenio es obligatorio.';
      fila.campos_error = ['CODIGO'];
    } else if (!fila.INSTITUCION) {
      fila.error = 'El nombre de la institución es obligatorio.';
      fila.campos_error = ['INSTITUCION'];
    } else if (texto(cruda.FECHA_INICIO) && !inicio) {
      fila.error = 'La fecha de inicio no tiene un formato válido (DD/MM/AAAA).';
      fila.campos_error = ['inicio'];
    } else if (texto(cruda.FECHA_FINALIZACION) && !fin) {
      fila.error = 'La fecha de finalización no tiene un formato válido (DD/MM/AAAA).';
      fila.campos_error = ['fin'];
    } else if (inicio && fin && fin.getTime() < inicio.getTime()) {
      fila.error = 'La fecha de finalización es anterior a la fecha de inicio.';
      fila.campos_error = ['inicio', 'fin'];
    } else if (vistos[clave]) {
      fila.error = 'Código duplicado: está repetido en el archivo (fila ' + vistos[clave] + ').';
      fila.campos_error = ['CODIGO'];
    } else if (existentes.indexOf(clave) >= 0) {
      fila.error = 'Código duplicado: ya existe un convenio registrado con ' + codigo + '.';
      fila.campos_error = ['CODIGO'];
    } else {
      let malo = COLUMNAS.filter(({ clave: c, tipo }) => tipo === 'lista' && fila[c])
        .filter(({ clave: c, opciones }) => !canonica(fila[c], opciones))[0];
      if (malo) {
        fila.error = `El valor de "${malo.etiqueta}" no es válido. Opciones: ${malo.opciones.join(', ')}.`;
        fila.campos_error = [malo.clave];
      } else if (fila.VIGENCIA && !soloEntero(fila.VIGENCIA)) {
        fila.error = 'La vigencia debe ser un número entero de años.';
        fila.campos_error = ['VIGENCIA'];
      } else if (fila.PAIS && lista_paises.length > 0 && !canonica(fila.PAIS, lista_paises)) {
        fila.error = `El país "${fila.PAIS}" no está registrado en la plataforma.`;
        fila.campos_error = ['PAIS'];
      }
    }

    if (clave && !vistos[clave]) vistos[clave] = fila.n;
    return fila;
  });
};
